import { Guide } from "@common/models";
import { log } from "../log";
import { ChecklistScheduleReason, Scheduler, GuideScheduleReason } from "../scheduler/scheduler";
import { TourPlaybackManager } from "./tourPlayback";
import { makeAutoObservable } from "mobx";
import { PersistentStorage } from "./persistent-storage/storage";
import { TourStartManager, TourStartStrategy } from "./tourStartManager";
import { globalEventBus } from "../events/eventBus";
import {
  TourLifecycleEventContext,
  TourLifecycleManagerEvents,
  TourLifecycleNamespace,
} from "./tourLifecycleManager.events";
import { EventEmitter } from "../events/eventEmitter";
import { TabManager } from "./tabManager";
import { PyneSettings } from "@common/schemas";
import { TourState } from "./persistent-storage/tourState";
import { TourSession, TourSessionManager } from "./tourSessionManager";
import { Checklist } from "@common/schemas/checklist";
import { GuideRegistry } from "@/stores/GuideRegistry";
import { ChecklistManager } from "../checklist/checklistManager";

export interface TourLifecycleManagerParams {
  pyneSettings: PyneSettings;
  scheduler: Scheduler;
  guideRegistry: GuideRegistry;
  storage: PersistentStorage;
  tourStartManager: TourStartManager;
  tabManager: TabManager;
  tourSessionManager: TourSessionManager;
  previewGuideId?: string;
  skipBlobMediaPreload?: boolean;
  mediaPlaybackRate?: number;
  pyneClient: PyneClient;
}

interface PyneClient {
  currentUser: { id: string; traits: Record<string, any> } | null;
  anonymousId: string | null;
}

export type TourTerminateReason =
  | "session_expired"
  | "trigger_from_debugger"
  | "trigger_from_pyne_client"
  | "tour_finished"
  | "tour_dismissed"
  | "restart_preview"
  | "close_preview"
  | "another_tour_triggered_from_checklist"
  | "fresh_preview_handover";

export type ChecklistTerminateReason = "checklist_finished" | "checklist_dismissed" | "close_preview";

export class TourLifecycleManager {
  private pyneSettings: PyneSettings;
  scheduler: Scheduler;
  private guideRegistry: GuideRegistry;
  private tabManager: TabManager;
  private storage: PersistentStorage;
  private pyneClient: PyneClient;
  public tourStartManager: TourStartManager;
  public currentGuide?: Guide;
  public currentTour?: TourPlaybackManager;
  public currentChecklist?: Checklist;
  public currentChecklistManager?: ChecklistManager;
  public lastTourStartStrategy?: TourStartStrategy;
  private tourSessionManager: TourSessionManager;
  private tourSwitched = false;
  private preventPauseOnTabChange = false;
  isChecklistVisibleFromAvatar = false;
  public status: "initialized" | "playingTour" | "tourFinished" = "initialized";
  public events = new EventEmitter<
    typeof TourLifecycleNamespace,
    TourLifecycleManagerEvents,
    TourLifecycleEventContext
  >(TourLifecycleNamespace);
  private skipBlobMediaPreload?: boolean;
  private mediaPlaybackRate?: number;
  private eventEnricherMiddlewareStopFn?: () => void;

  constructor({
    pyneSettings,
    scheduler,
    guideRegistry,
    storage,
    tourStartManager,
    tabManager,
    tourSessionManager,
    skipBlobMediaPreload,
    mediaPlaybackRate,
    pyneClient,
  }: TourLifecycleManagerParams) {
    makeAutoObservable(this);

    this.storage = storage;
    this.pyneSettings = pyneSettings;
    this.tourStartManager = tourStartManager;
    this.tabManager = tabManager;
    this.skipBlobMediaPreload = skipBlobMediaPreload;
    this.mediaPlaybackRate = mediaPlaybackRate;
    this.tourSessionManager = tourSessionManager;
    this.guideRegistry = guideRegistry;
    this.pyneClient = pyneClient;
    this.storage.sessionState.setVisitedAt();

    this.scheduler = scheduler;
    this.scheduler.events.on("guideScheduled", ({ payload: { guide, scheduleReason, tourState } }) => {
      this.startTour({ guide, scheduleReason, tourState });
    });

    this.scheduler.events.on("checklistScheduled", ({ payload: { checklist, scheduleReason } }) => {
      this.startChecklist({ checklist, scheduleReason });
    });

    this.tabManager.events.on("currentTabActivated", () => {
      this.startScheduler();
    });

    this.tabManager.events.on("currentTabInactivated", () => {
      this.stopScheduler();
      if (this.preventPauseOnTabChange) {
        this.preventPauseOnTabChange = false;
        return;
      }
      this.currentTour?.pauseTour();
    });

    this.tabManager.events.on("otherTabTookoverTheTour", () => {
      this.currentTour?.suspendTour("tourPlayingOnOtherTab");
    });

    this.tourSessionManager.events.on("tourSessionExpired", ({ payload: { sessionId } }) => {
      if (this.tabManager.amIActive()) {
        this.terminateCurrentTourAndLookForOthers("session_expired");
      } else {
        this.terminateCurrentTour("session_expired");
      }
    });
  }

  get isTourPlaying() {
    return (
      !!this.currentTour &&
      !(
        this.currentTour.status === "finished" ||
        this.currentTour.status === "terminated" ||
        this.currentTour.status === "dismissed" ||
        this.currentTour.status === undefined
      )
    );
  }

  terminateCurrentTour(reason: TourTerminateReason) {
    if (!this.currentTour) {
      return;
    }

    this.tourSessionManager.terminateTourSession(reason);
    this.currentTour?.terminateTour();
    this.clearCurrentTour();
  }

  terminateCurrentChecklist(reason: ChecklistTerminateReason) {
    if (!this.currentChecklistManager) {
      return;
    }

    this.currentChecklistManager.terminateChecklist(reason);
  }

  terminateCurrentTourAndLookForOthers(terminateReason: TourTerminateReason) {
    this.terminateCurrentTour(terminateReason);
    this.lookForOtherGuides();
  }

  start() {
    log("starting tour lifecycle manager");
    this.startScheduler();
  }

  private startScheduler() {
    if (!this.tabManager.amIActive()) {
      return;
    }

    this.scheduler.start();
  }

  stop() {
    this.scheduler.stop();
  }

  // if we cause a focus change, we should prevent the tour from pausing
  handleQuestionChoiceSelected(choiceType: string) {
    if (choiceType === "link") {
      this.preventPauseOnTabChange = true;
    }
  }

  async terminateCurrentTourAndStartNewTourByGuideId(
    guideId: string,
    reason: GuideScheduleReason,
    terminateReason: TourTerminateReason,
    options?: { tourStartStrategy?: TourStartStrategy }
  ) {
    const guide = this.guideRegistry.findById(guideId);
    if (!guide) {
      return;
    }

    await this.terminateCurrentTourAndStartNewTour(new Guide(guide), reason, terminateReason, options);
  }

  async terminateCurrentTourAndStartNewTour(
    guide: Guide,
    reason: GuideScheduleReason,
    terminateReason: TourTerminateReason,
    options?: { tourStartStrategy?: TourStartStrategy }
  ) {
    this.terminateCurrentTour(terminateReason);
    await this.startTour({ guide, scheduleReason: reason }, options);
  }

  async startTour(
    { guide, scheduleReason, tourState }: { guide: Guide; scheduleReason: GuideScheduleReason; tourState?: TourState },
    options?: { tourStartStrategy?: TourStartStrategy }
  ) {
    if (this.currentTour) {
      return;
    }

    this.stopScheduler();

    this.eventEnricherMiddlewareStopFn?.();
    this.eventEnricherMiddlewareStopFn = globalEventBus.use("*", (context) => {
      return { guide, ...context };
    });

    if (!guide.tour_playback) {
      throw new Error("Guide does not have tour playback data");
    }

    const tourStartStrategy = await this.tourStartManager.decideStrategy(
      this.tabManager.tabId,
      guide.tour_playback,
      tourState
    );
    this.lastTourStartStrategy = tourStartStrategy;

    const sessionInfo = this.tourSessionManager.initializeTourSession(scheduleReason);

    const tourPlaybackManager = this.buildTourPlaybackManager(
      guide,
      options?.tourStartStrategy ?? tourStartStrategy,
      sessionInfo
    );
    this.currentGuide = guide;
    this.currentTour = tourPlaybackManager;

    tourPlaybackManager.events.on("started", () => this.events.emit("tourStarted", { guide }));

    tourPlaybackManager.events.on("finished", () => this.events.emit("tourFinished", { guide }));

    tourPlaybackManager.events.on("terminated", () => {
      this.events.emit("tourTerminated", { guide });
    });

    tourPlaybackManager.events.on("dismissed", () => {
      this.events.emit("tourDismissed", { guide });
    });

    this.events.emit("tourInitialized", { guide, tour: tourPlaybackManager });

    log("starting tour");

    tourPlaybackManager.start();
    this.tourSwitched = false;
    this.status = "playingTour";
  }

  startChecklist({ checklist, scheduleReason }: { checklist: Checklist; scheduleReason: ChecklistScheduleReason }) {
    const checklistManager = new ChecklistManager({
      checklist,
      checklistState: this.storage.checklistState,
      userState: this.storage.userState,
      isPreview: scheduleReason === "preview" || scheduleReason === "trigger_from_debugger",
    });
    this.currentChecklist = checklist;
    this.currentChecklistManager = checklistManager;
    this.events.emit("checklistInitialized", { checklist, checklistManager });

    checklistManager.events.on("checklistStarted", ({ payload: { checklist, scheduleReason } }) => {
      this.events.emit("checklistStarted", { checklist, scheduleReason });
    });

    checklistManager.events.on("checklistDismissed", ({ payload: { checklist } }) => {
      this.scheduler.clearCurrentChecklist();
      this.clearCurrentChecklist();
      this.events.emit("checklistDismissed", {
        checklist,
        completionState: checklistManager.currentCompletionState,
      });
    });

    checklistManager.events.on("checklistCompleted", ({ payload: { checklist } }) => {
      this.scheduler.clearCurrentChecklist();
      this.clearCurrentChecklist();
      this.events.emit("checklistCompleted", { checklist });
    });

    checklistManager.events.on("checklistTerminated", ({ payload: { checklist } }) => {
      this.scheduler.clearCurrentChecklist();
      this.clearCurrentChecklist();
      this.events.emit("checklistTerminated", { checklist });
    });

    checklistManager.startChecklist(scheduleReason);

    this.events.emit("checklistDisplayed", { checklist, completionState: checklistManager.currentCompletionState });
  }

  clearCurrentChecklist() {
    this.currentChecklistManager = undefined;
    this.currentChecklist = undefined;
  }

  buildTourPlaybackManager(
    guide: Guide,
    tourStartStrategy: TourStartStrategy,
    session: TourSession
  ): TourPlaybackManager {
    if (!guide.tour_playback) {
      throw new Error("Guide does not have tour playback data");
    }

    const tourPlaybackManager = new TourPlaybackManager({
      pyneSettings: this.pyneSettings,
      guideId: guide.id,
      tourPlayback: guide.tour_playback,
      startStrategy: tourStartStrategy,
      settings: guide.settings,
      shouldAutoPlayFirstStep:
        guide.settings.should_auto_play_first_step || this.tourSwitched || tourStartStrategy.type !== "startOver",
      avatarStateStorage: this.storage.avatarState,
      tourStateStorage: this.storage.tourState,
      userStateStorage: this.storage.userState,
      skipBlobMediaPreload: this.skipBlobMediaPreload,
      mediaPlaybackRate: this.mediaPlaybackRate,
      tabId: this.tabManager.tabId,
      sessionId: session.sessionId,
      startedAt: session.startedAt,
      organizationId: guide.organization_id,
      pyneClient: this.pyneClient,
    });

    tourPlaybackManager.events.on("finished", () => {
      this.handleTourFinished();
    });

    tourPlaybackManager.events.on("finishCriteriaMet", () => {
      this.handleTourFinishCriteriaMet();
    });

    tourPlaybackManager.events.on("dismissed", () => {
      this.handleTourDismissed();
    });

    tourPlaybackManager.events.on("questionChoiceSelected", ({ payload: { choiceType } }) => {
      this.handleQuestionChoiceSelected(choiceType);
    });

    tourPlaybackManager.events.on("tourSwitched", ({ payload: { guideId } }) => {
      this.handleTourSwitch(guideId);
    });

    return tourPlaybackManager;
  }

  dismissCurrentTour() {
    this.currentTour?.dismissTour();
  }

  clearState() {
    this.storage.clearTourRelatedState();
  }

  private handleTourSwitch(guideId: string) {
    log("tour switch", guideId);
    this.currentTour?.finishTour();
    this.tourSwitched = true;
    this.scheduler.switchGuide(guideId, "trigger_from_other_tour");
  }

  forceStartTour(guideId: string, reason: "trigger_from_pyne_client", terminateReason: TourTerminateReason) {
    if (this.currentGuide?.id === guideId) {
      return;
    }

    this.terminateCurrentTour(terminateReason);
    this.scheduler.switchGuide(guideId, reason);
  }

  showChecklistFromAvatar() {
    if (this.currentChecklistManager && this.currentChecklist) {
      this.currentTour?.pauseTour();
      this.currentChecklistManager.expandChecklist();
      this.isChecklistVisibleFromAvatar = true;
      this.events.emit("checklistExpandedFromAvatar", {
        checklist: this.currentChecklist,
        completionState: this.currentChecklistManager.currentCompletionState,
      });
    }
  }

  hideChecklistFromAvatar() {
    if (this.currentChecklistManager && this.currentChecklist) {
      this.isChecklistVisibleFromAvatar = false;
      this.currentChecklistManager.minimizeChecklist();
    }
  }

  onTourStartClickedFromChecklist(guideId: string) {
    const currentChecklist = this.currentChecklist;
    const completionState = this.currentChecklistManager?.currentCompletionState ?? [];

    this.isChecklistVisibleFromAvatar = false;
    this.terminateCurrentTourAndStartNewTourByGuideId(
      guideId,
      "trigger_from_checklist",
      "another_tour_triggered_from_checklist"
    );

    if (currentChecklist) {
      this.events.emit("tourStartTriggeredFromChecklist", {
        guideId,
        checklist: currentChecklist,
        completionState,
      });
    }
  }

  onChecklistMinimizeClicked() {
    if (this.isChecklistVisibleFromAvatar) {
      this.hideChecklistFromAvatar();
    } else {
      this.currentChecklistManager?.minimizeChecklist();
    }

    if (this.currentChecklist) {
      this.events.emit("checklistMinimized", {
        checklist: this.currentChecklist,
        completionState: this.currentChecklistManager?.currentCompletionState ?? [],
      });
    }
  }

  expandCurrentChecklist() {
    if (this.currentChecklistManager && this.currentChecklist) {
      this.currentChecklistManager.expandChecklist();
      this.events.emit("checklistExpanded", {
        checklist: this.currentChecklist,
        completionState: this.currentChecklistManager?.currentCompletionState ?? [],
      });
    }
  }

  dismissCurrentChecklist() {
    if (this.currentChecklistManager && this.currentChecklist) {
      this.currentChecklistManager.dismissChecklist();
      this.events.emit("checklistDismissed", {
        checklist: this.currentChecklist,
        completionState: this.currentChecklistManager?.currentCompletionState ?? [],
      });
    }
  }

  private handleTourFinished() {
    log("tour finished");
    this.terminateCurrentTourAndLookForOthers("tour_finished");
    this.status = "tourFinished";
  }

  private handleTourFinishCriteriaMet() {
    log("tour finish criteria met");
    this.storage.userState.addFinishedGuide(this.currentGuide!.id);
  }

  private handleTourDismissed() {
    log("tour dismissed");
    this.storage.userState.addDismissedGuide(this.currentGuide!.id);
    this.terminateCurrentTourAndLookForOthers("tour_dismissed");
    this.status = "tourFinished";
  }

  private clearCurrentTour() {
    this.currentGuide = undefined;
    this.currentTour = undefined;
    this.lastTourStartStrategy = undefined;
    this.storage.clearTourState();
  }

  private lookForOtherGuides() {
    this.clearCurrentTour();
    this.scheduler.clearCurrentGuide();
    this.startScheduler();
  }

  private stopScheduler() {
    this.scheduler.stop();
  }
}
